import {
  Icons,
  EntityAction,
  EntityActions,
  notify,
} from '@cast/design-system';
import { parseApiError } from '@cast/api-client';
import { Node, NodeRoles } from '@cast/types';

import { useSimulatedClusterDemoUi } from '@cast/infrastructure-demo';
import { useAbility } from '@cast/shared-ability-data-access';
import { useDeleteNodeDialog } from 'hooks/dialogs';
import { useInterruptNodeMutation } from 'hooks/mutations';
import { useDrainNodeMutation } from 'hooks/mutations/nodes';
import { useKentDrainNodeMutation } from 'hooks/mutations/kent/useKentDrainNodeMutation';
import { useKentInterruptNodeMutation } from 'hooks/mutations/kent/useKentInterruptNodeMutation';
import { Cluster } from 'types/cluster';

import {
  isKOPSCluster,
  isAnywhereCluster,
  isKentEligibleCluster,
  isKentManagedCluster,
} from '@cast/shared-cluster-context-data-access';

type Props = {
  cluster: Cluster;
  node: Node;
  masterNodesCount: number;
};

export const NodeActionsMenu = ({ cluster, node, masterNodesCount }: Props) => {
  const { name, id, spotConfig, role } = node;
  const clusterId = cluster.id;
  const isKops = isKOPSCluster(cluster);
  const isAnywhere = isAnywhereCluster(cluster);
  const isSimulatedClusterDemoUi = useSimulatedClusterDemoUi();

  const interruptNodeMutation = useInterruptNodeMutation(clusterId);
  const drainNodeMutation = useDrainNodeMutation();
  const kentInterruptNodeMutation = useKentInterruptNodeMutation();
  const kentDrainNodeMutation = useKentDrainNodeMutation();

  const [canEdit, canDelete] = useAbility().canMany([
    ['update', 'ClusterNodes'],
    ['delete', 'ClusterNodes'],
  ]);

  const { openDeleteNodeDialog } = useDeleteNodeDialog();
  const deleteDisabled =
    (role === NodeRoles.master && masterNodesCount === 1) ||
    (role === NodeRoles.master && isKops);

  const isSpot = spotConfig?.isSpot;

  const isKentManaged = isKentManagedCluster(cluster);

  const isMenuDisabled =
    isSimulatedClusterDemoUi ||
    isAnywhere ||
    (deleteDisabled && !isSpot && !clusterId) ||
    (isKentEligibleCluster(cluster) && !isKentManagedCluster(cluster));

  const handleInterruptNode = () => {
    if (isKentManaged) {
      kentInterruptNodeMutation.mutate(
        { clusterId, nodeName: name, nodeId: id },
        {
          onSuccess: () => {
            notify.success('Node interrupt simulation initiated');
          },
          onError: (error) => {
            notify.error(
              parseApiError(error, 'Failed to initiate Spot Instance interrupt')
            );
          },
        }
      );
    } else {
      interruptNodeMutation.mutate(id);
    }
  };

  const handleDrainNode = () => {
    if (isKentManaged) {
      kentDrainNodeMutation.mutate(
        { clusterId, nodeName: name, nodeId: id },
        {
          onSuccess: () => {
            notify.success('Node draining initiated successfully');
          },
          onError: (error) => {
            notify.error(
              parseApiError(error, 'Failed to initiate node draining')
            );
          },
        }
      );
    } else {
      drainNodeMutation.mutate({ nodeId: id, clusterId });
    }
  };

  const handleDeleteNode = () => {
    openDeleteNodeDialog({ cluster, nodes: [node] });
  };

  return (
    <EntityActions
      triggerButtonProps={{
        disabled: isMenuDisabled,
      }}
      testId="node-actions"
    >
      {isSpot && (
        <EntityAction
          disabled={!canEdit || !clusterId}
          onClick={handleInterruptNode}
          startAdornment={<Icons.XCircle />}
          tooltipProps={{
            title: 'Simulate Spot Instance interrupt',
            placement: 'top',
            arrow: true,
          }}
          testId="node-interrupt-action"
        >
          Interrupt node
        </EntityAction>
      )}
      <EntityAction
        disabled={!canEdit}
        onClick={handleDrainNode}
        startAdornment={<Icons.HourglassSimpleLow />}
        testId="node-drain-action"
      >
        Drain node
      </EntityAction>
      <EntityAction
        disabled={deleteDisabled || !canDelete}
        onClick={handleDeleteNode}
        startAdornment={<Icons.TrashSimple />}
        disruptive
        testId="node-delete-action"
      >
        Delete node
      </EntityAction>
    </EntityActions>
  );
};
